import { useCallback, useEffect, useState } from 'react';
import { Check, Copy, Download, ExternalLink, Link2, Loader2, MessageCircle, RefreshCw, Trash2, UserPlus } from 'lucide-react';
import { downloadDocument, type SignableDocument } from '@/lib/procuracaoPdf';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { chargeWhatsappLink, isUsablePhone } from '@/lib/overdueCharges';
import {
  formatCep,
  formatCpf,
  formatPhone,
  generateIntakeToken,
  INTAKE_BUCKET,
  INTAKE_DAYS,
  INTAKE_KIND_LABELS,
  intakeFileKind,
  intakeInvite,
  intakeToClient,
  intakeUrl,
  onlyDigits,
  type IntakeData,
} from '@/lib/clientIntake';

interface Intake {
  id: string;
  label: string;
  phone: string | null;
  status: 'aberto' | 'enviado' | 'aprovado' | 'cancelado';
  expires_at: string;
  data: IntakeData | null;
  consent_at: string | null;
  submitted_at: string | null;
  created_at: string;
  client_id: string | null;
}

const db = supabase as any;
const formatDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '');

const statusOf = (intake: Intake) => {
  if (intake.status === 'enviado') return { label: 'Aguardando sua revisão', className: 'bg-[#c4ab83]/25 text-[#7d6640]' };
  if (intake.status === 'aprovado') return { label: 'Virou cliente', className: 'bg-emerald-50 text-emerald-700' };
  if (intake.status === 'cancelado') return { label: 'Cancelado', className: 'bg-slate-100 text-slate-500' };
  if (new Date(intake.expires_at).getTime() < Date.now()) return { label: 'Link expirado', className: 'bg-slate-100 text-slate-500' };
  return { label: `Aguardando a pessoa até ${formatDate(intake.expires_at)}`, className: 'bg-slate-100 text-slate-600' };
};

const Row = ({ label, value }: { label: string; value?: string | null }) =>
  value ? (
    <div className="flex justify-between gap-4 py-1 text-sm">
      <dt className="shrink-0 text-slate-500">{label}</dt>
      <dd className="min-w-0 break-words text-right font-medium text-slate-800">{value}</dd>
    </div>
  ) : null;

export type IntakePanelMode = 'link' | 'recebidos';

interface ClientIntakePanelProps {
  mode: IntakePanelMode | null;
  onClose: () => void;
  onClientCreated: () => void;
  onPendingCount: (count: number) => void;
}

export const ClientIntakePanel = ({ mode, onClose, onClientCreated, onPendingCount }: ClientIntakePanelProps) => {
  const open = mode !== null;
  // Mantém o conteúdo enquanto a janela fecha, sem trocar de lista na animação
  const [lastMode, setLastMode] = useState<IntakePanelMode>('recebidos');
  useEffect(() => {
    if (mode) setLastMode(mode);
  }, [mode]);
  const showReceived = (mode || lastMode) === 'recebidos';
  const { toast } = useToast();
  const { user } = useAuth();
  const [intakes, setIntakes] = useState<Intake[]>([]);
  const [loading, setLoading] = useState(false);
  const [label, setLabel] = useState('');
  const [phone, setPhone] = useState('');
  const [creating, setCreating] = useState(false);
  const [fresh, setFresh] = useState<{ url: string; label: string; phone: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [review, setReview] = useState<Intake | null>(null);
  const [files, setFiles] = useState<{ name: string; kind: string; path: string }[]>([]);
  const [approving, setApproving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await db.from('client_intakes').select('id, label, phone, status, expires_at, data, consent_at, submitted_at, created_at, client_id').order('created_at', { ascending: false });
    if (error) toast({ title: 'Não foi possível carregar as entradas', description: error.message, variant: 'destructive' });
    setIntakes(data || []);
    if (!error) onPendingCount((data || []).filter((i: Intake) => i.status === 'enviado').length);
    setLoading(false);
  }, [toast, onPendingCount]);

  // Carrega já ao abrir a página, para o botão mostrar quantos cadastros esperam revisão
  useEffect(() => {
    load();
  }, [load, open]);

  const visible = intakes
    .filter((i) => (showReceived ? i.status === 'enviado' || i.status === 'aprovado' : i.status === 'aberto' || i.status === 'cancelado'))
    .sort((a, b) => Number(b.status === 'enviado') - Number(a.status === 'enviado'));

  const expiry = () => new Date(Date.now() + INTAKE_DAYS * 86400000).toISOString();

  const create = async () => {
    if (!user || !label.trim()) return;
    setCreating(true);
    try {
      const { token, hash } = await generateIntakeToken();
      const { error } = await db.from('client_intakes').insert({ user_id: user.id, label: label.trim(), phone: phone.trim() || null, token_hash: hash, expires_at: expiry() });
      if (error) throw error;
      setFresh({ url: intakeUrl(window.location.origin, token), label: label.trim(), phone: phone.trim() });
      setCopied(false);
      setLabel('');
      setPhone('');
      load();
    } catch (e) {
      toast({ title: 'Não foi possível criar o link', description: (e as Error).message, variant: 'destructive' });
    } finally {
      setCreating(false);
    }
  };

  // O link completo não fica salvo: para enviar de novo, gera outro e o anterior deixa de funcionar
  const renew = async (intake: Intake) => {
    try {
      const { token, hash } = await generateIntakeToken();
      const { error } = await db.from('client_intakes').update({ token_hash: hash, expires_at: expiry(), updated_at: new Date().toISOString() }).eq('id', intake.id).eq('status', 'aberto');
      if (error) throw error;
      setFresh({ url: intakeUrl(window.location.origin, token), label: intake.label, phone: intake.phone || '' });
      setCopied(false);
      load();
    } catch (e) {
      toast({ title: 'Não foi possível gerar o link', description: (e as Error).message, variant: 'destructive' });
    }
  };

  const copy = async () => {
    if (!fresh) return;
    try {
      await navigator.clipboard.writeText(fresh.url);
      setCopied(true);
    } catch {
      toast({ title: 'Não foi possível copiar', description: 'Selecione o link e copie manualmente.', variant: 'destructive' });
    }
  };

  const openReview = async (intake: Intake) => {
    setReview(intake);
    setFiles([]);
    const { data } = await supabase.storage.from(INTAKE_BUCKET).list(intake.id, { limit: 100 });
    setFiles((data || []).map((f) => ({ name: f.name, kind: intakeFileKind(f.name), path: `${intake.id}/${f.name}` })));
  };

  const openFile = async (path: string) => {
    // Abre a aba já no clique, para o navegador não bloquear
    const tab = window.open('', '_blank', 'noopener=no');
    const { data, error } = await supabase.storage.from(INTAKE_BUCKET).createSignedUrl(path, 300);
    if (error || !data?.signedUrl) {
      tab?.close();
      toast({ title: 'Não foi possível abrir o arquivo', variant: 'destructive' });
      return;
    }
    if (tab) tab.location.href = data.signedUrl;
  };

  const approve = async () => {
    if (!review?.data || !user) return;
    setApproving(true);
    try {
      const { data: existing } = await db.from('clients').select('id, name, cpf_cnpj').not('cpf_cnpj', 'is', null);
      const duplicate = (existing || []).find((c: any) => onlyDigits(c.cpf_cnpj) === onlyDigits(review.data!.cpf));
      if (duplicate) throw new Error(`Já existe um cadastro com este CPF: ${duplicate.name}.`);
      const { data: client, error } = await db.from('clients').insert(intakeToClient(review.data, user.id, review.consent_at)).select('id').single();
      if (error) throw error;
      const { error: updateError } = await db.from('client_intakes').update({ status: 'aprovado', client_id: client.id, updated_at: new Date().toISOString() }).eq('id', review.id);
      if (updateError) throw updateError;
      toast({ title: 'Cliente criado', description: review.data.nome });
      setReview(null);
      load();
      onClientCreated();
    } catch (e) {
      toast({ title: 'Não foi possível criar o cliente', description: (e as Error).message, variant: 'destructive' });
    } finally {
      setApproving(false);
    }
  };

  const remove = async (intake: Intake) => {
    if (!window.confirm(`Excluir a entrada de ${intake.label}, com os dados e documentos enviados? Isso não pode ser desfeito.`)) return;
    try {
      const { data: stored } = await supabase.storage.from(INTAKE_BUCKET).list(intake.id, { limit: 100 });
      if (stored?.length) {
        const { error: storageError } = await supabase.storage.from(INTAKE_BUCKET).remove(stored.map((f) => `${intake.id}/${f.name}`));
        if (storageError) throw storageError;
      }
      const { error } = await db.from('client_intakes').delete().eq('id', intake.id);
      if (error) throw error;
      setReview(null);
      load();
    } catch (e) {
      toast({ title: 'Não foi possível excluir', description: (e as Error).message, variant: 'destructive' });
    }
  };

  const data = review?.data;

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DialogContent className="w-[95vw] max-w-2xl rounded-2xl bg-white">
          <DialogHeader>
            <DialogTitle>{showReceived ? 'Cadastros recebidos' : 'Link de cadastro'}</DialogTitle>
            <DialogDescription>
              {showReceived
                ? 'Quem preencheu pelo link aparece aqui. Revise os dados e os documentos antes de criar o cliente.'
                : 'Gere um link para a pessoa preencher os dados e enviar os documentos.'}
            </DialogDescription>
          </DialogHeader>

          {!showReceived && (
          <div className="space-y-3 rounded-xl border border-slate-200 p-4">
            <div className="grid gap-3 xl:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="intake-label">Nome da pessoa</Label>
                <Input id="intake-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Ex: Maria da Silva" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="intake-phone">WhatsApp <span className="font-normal text-slate-400">(opcional)</span></Label>
                <Input id="intake-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(81) 99999-9999" />
              </div>
            </div>
            <Button onClick={create} disabled={creating || !label.trim()} className="w-full bg-slate-900 text-white hover:bg-slate-800 xl:w-auto">
              {creating ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Link2 className="mr-1.5 h-4 w-4 text-[#e8d5a8]" />}Gerar link
            </Button>

            {fresh && (
              <div className="space-y-2 rounded-xl border border-[#c4ab83]/50 bg-[#c4ab83]/10 p-3">
                <p className="text-sm font-semibold text-slate-900">Link de {fresh.label}</p>
                <Input readOnly value={fresh.url} aria-label="Link de cadastro" onFocus={(e) => e.target.select()} className="bg-white text-xs" />
                <p className="text-xs text-slate-600">Por segurança, este link só aparece agora. Se perder, gere outro na lista abaixo.</p>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={copy}>
                    {copied ? <Check className="mr-1.5 h-3.5 w-3.5 text-emerald-600" /> : <Copy className="mr-1.5 h-3.5 w-3.5" />}{copied ? 'Copiado' : 'Copiar'}
                  </Button>
                  {isUsablePhone(fresh.phone) && (
                    <Button asChild size="sm" className="bg-slate-900 text-white hover:bg-slate-800">
                      <a href={chargeWhatsappLink(fresh.phone, intakeInvite(fresh.label, fresh.url))} target="_blank" rel="noopener noreferrer"><MessageCircle className="mr-1.5 h-3.5 w-3.5 text-[#e8d5a8]" />Abrir no WhatsApp</a>
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
          )}

          {!showReceived && visible.length > 0 && <p className="text-sm font-semibold text-slate-800">Links enviados</p>}

          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[#9a7f52]" /></div>
          ) : visible.length === 0 ? (
            <p className="py-4 text-center text-sm text-slate-500">{showReceived ? 'Nenhum cadastro recebido ainda.' : 'Nenhum link aguardando resposta.'}</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {visible.map((intake) => {
                const status = statusOf(intake);
                return (
                  <li key={intake.id} className="flex flex-wrap items-center justify-between gap-2 py-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-slate-900">{intake.data?.nome || intake.label}</p>
                      <span className={`mt-0.5 inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${status.className}`}>{status.label}</span>
                    </div>
                    <div className="flex gap-1">
                      {intake.status === 'aberto' && <Button size="sm" variant="outline" onClick={() => renew(intake)}><RefreshCw className="mr-1.5 h-3.5 w-3.5" />Novo link</Button>}
                      {(intake.status === 'enviado' || intake.status === 'aprovado') && <Button size="sm" variant="outline" onClick={() => openReview(intake)}>{intake.status === 'enviado' ? 'Revisar' : 'Ver'}</Button>}
                      {intake.status !== 'aprovado' && <Button size="icon" variant="ghost" onClick={() => remove(intake)} aria-label={`Excluir entrada de ${intake.label}`}><Trash2 className="h-4 w-4 text-slate-400" /></Button>}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!review} onOpenChange={(isOpen) => !isOpen && setReview(null)}>
        <DialogContent className="w-[95vw] max-w-lg rounded-2xl bg-white">
          <DialogHeader>
            <DialogTitle>{data?.nome || review?.label}</DialogTitle>
            <DialogDescription>Enviado em {formatDate(review?.submitted_at)}. Consentimento LGPD registrado em {formatDate(review?.consent_at)}.</DialogDescription>
          </DialogHeader>
          {data && (
            <dl className="divide-y divide-slate-100">
              <Row label="CPF" value={formatCpf(data.cpf)} />
              <Row label="RG" value={data.rg} />
              <Row label="Nascimento" value={data.nascimento.split('-').reverse().join('/')} />
              <Row label="Estado civil" value={data.estado_civil} />
              <Row label="Profissão" value={data.profissao} />
              <Row label="Nacionalidade" value={data.nacionalidade} />
              <Row label="Celular" value={formatPhone(data.celular)} />
              <Row label="E-mail" value={data.email} />
              <Row label="Endereço" value={`${data.endereco}, ${data.numero}${data.complemento ? `, ${data.complemento}` : ''}, ${data.bairro}, ${data.cidade}/${data.uf}, CEP ${formatCep(data.cep)}`} />
            </dl>
          )}
          <div>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-semibold text-slate-800">Documentos</p>
              {data && (
                <div className="flex flex-wrap gap-2">
                  {([['procuracao', 'Gerar procuração'], ['hipossuficiencia', 'Gerar declaração']] as [SignableDocument, string][]).map(([kind, text]) => (
                    <Button key={kind} type="button" size="sm" variant="outline" onClick={() => downloadDocument(kind, data).catch(() => toast({ title: 'Não foi possível gerar o documento', variant: 'destructive' }))}>
                      <Download className="mr-1.5 h-3.5 w-3.5" />{text}
                    </Button>
                  ))}
                </div>
              )}
            </div>
            {files.length === 0 ? (
              <p className="text-sm text-slate-500">Carregando...</p>
            ) : (
              <ul className="space-y-1">
                {files.map((file, index) => (
                  <li key={file.path}>
                    <button type="button" onClick={() => openFile(file.path)} className="flex w-full items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2 text-left text-sm text-slate-700 hover:bg-[#c4ab83]/10">
                      {INTAKE_KIND_LABELS[file.kind] || 'Documento'} {index + 1}<ExternalLink className="h-3.5 w-3.5 shrink-0 text-[#9a7f52]" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {review?.status === 'enviado' && (
            <Button onClick={approve} disabled={approving} className="w-full bg-slate-900 text-white hover:bg-slate-800">
              {approving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <UserPlus className="mr-1.5 h-4 w-4 text-[#e8d5a8]" />}Criar cliente com estes dados
            </Button>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
